import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { Clock3, GripVertical, Loader2, MapPin, RefreshCw, Trophy } from "lucide-react";
import { toast } from "react-toastify";

import axiosInstance from "src/common/axiosInstance";
import { Monogram } from "src/stage/StageUi";
import { apiMessage, moveLeadToStage, stageTones } from "src/features/Stage/shared/crmWork";
import { formatLeadFieldValue, leadCustomValue } from "../../utils/leadFields";

// AUTO-CRM-32: the pipeline as a board. One column per stage; drag a card to
// move the lead (POST /lead-workflow/{lead}/stage). Filters and search from
// the list apply here too.
const PAGE = 25;
const DAY = 86400000;
const VALUE_FIELD = /budget|value|amount|deal|price|revenue/i;

const money = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || !number) return null;
  if (number >= 10000000) return `₹${(number / 10000000).toFixed(number >= 100000000 ? 0 : 1)} Cr`;
  if (number >= 100000) return `₹${(number / 100000).toFixed(number >= 1000000 ? 0 : 1)} L`;
  return `₹${number.toLocaleString("en-IN")}`;
};

function daysIn(lead) {
  const since = Date.parse(lead.stage_changed_at || lead.created_at || "");
  return Number.isFinite(since) ? Math.max(0, Math.floor((Date.now() - since) / DAY)) : null;
}

function LeadCard({ lead, valueField, chipField, onOpen, dragging = false, overlay = false }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `lead-${lead.id}`, data: { lead }, disabled: overlay });
  const days = daysIn(lead);
  const value = valueField ? money(leadCustomValue(lead, valueField.key)) : null;
  const chip = chipField ? leadCustomValue(lead, chipField.key) : null;
  const where = [lead.company, lead.company_location].filter(Boolean).join(" · ");
  // Mouse and touch drag the whole card; the keyboard drags from the handle so Enter still opens the lead.
  const { onPointerDown, onKeyDown } = listeners || {};
  return (
    <article
      ref={overlay ? undefined : setNodeRef}
      className={`crm-card${isDragging || dragging ? " is-dragging" : ""}${overlay ? " is-overlay" : ""}`}
      onPointerDown={overlay ? undefined : onPointerDown}
    >
      <button type="button" className="crm-card-open" onClick={() => onOpen?.(lead.id)} aria-label={`Open ${lead.name}`}>
        <span className="crm-card-name">{lead.name || "Unnamed lead"}</span>
        {where && <span className="crm-card-sub"><MapPin size={11} aria-hidden="true" />{where}</span>}
        <span className="crm-card-tags">
          {value && <span className="crm-card-value">{value}</span>}
          {chip && <span className={`crm-card-chip is-${String(chip).toLowerCase()}`}>{formatLeadFieldValue(chipField, chip)}</span>}
          {lead.source && <span className="crm-card-chip">{lead.source}</span>}
          {Number(lead.duplicate_count) > 0 && <span className="crm-card-chip is-warn" title="Another lead has the same phone or email">Possible duplicate</span>}
        </span>
      </button>
      <span className="crm-card-foot">
        {lead.assigned_user?.name
          ? <span className="crm-card-owner" title={`Owner: ${lead.assigned_user.name}`}><Monogram name={lead.assigned_user.name} size={22} /><span>{lead.assigned_user.name.split(/\s+/)[0]}</span></span>
          : <span className="crm-card-owner is-none" title="Nobody owns this lead yet"><span className="crm-card-noowner" aria-hidden="true" />No owner</span>}
        {days !== null && <span className={`crm-card-age${days >= 14 ? " is-stale" : days >= 7 ? " is-slow" : ""}`} title={`${days} days in this stage`}><Clock3 size={11} aria-hidden="true" />{days ? `${days}d` : "today"}</span>}
        {!overlay && <span className="crm-card-grip" {...attributes} onKeyDown={onKeyDown} aria-label={`Move ${lead.name} to another stage`}><GripVertical size={14} /></span>}
      </span>
    </article>
  );
}

function Column({ stage, tone, column, valueField, chipField, onOpen, onMore, hidden }) {
  const { setNodeRef, isOver } = useDroppable({ id: `stage-${stage.id}`, data: { stage }, disabled: hidden });
  const total = column?.total ?? 0;
  const sum = valueField ? (column?.leads || []).reduce((acc, lead) => acc + (Number(leadCustomValue(lead, valueField.key)) || 0), 0) : 0;
  return (
    <section className={`crm-col${isOver ? " is-over" : ""}${stage.is_closed ? " is-closed" : ""}`} style={{ "--tone": tone.color, "--tone-soft": tone.soft }} aria-label={`${stage.name}, ${total} leads`}>
      <header className="crm-col-head">
        <span className="crm-col-bar" aria-hidden="true" />
        <div className="crm-col-title">
          <h3>{tone.kind === "won" && <Trophy size={13} aria-hidden="true" />}{stage.name}</h3>
          <span className="crm-col-count ws-num">{column ? total : "…"}</span>
        </div>
        <div className="crm-col-meta">
          {!stage.is_closed && stage.probability !== undefined && stage.probability !== null && <span title="Chance a lead here becomes a sale">{stage.probability}% likely</span>}
          {sum > 0 && <span className="ws-num" title={`Total ${valueField.label} of the cards shown`}>{money(sum)}</span>}
        </div>
      </header>
      <div ref={setNodeRef} className="crm-col-body">
        {hidden ? <p className="crm-col-empty">Hidden by the stage filter</p>
          : !column ? [0, 1, 2].map((key) => <span key={key} className="ws-skeleton crm-card-skel" />)
            : column.error ? <p className="crm-col-empty">Couldn’t load this stage.</p>
              : column.leads.length === 0 ? <p className="crm-col-empty">{isOver ? "Drop to move here" : "No leads here"}</p>
                : column.leads.map((lead) => <LeadCard key={lead.id} lead={lead} valueField={valueField} chipField={chipField} onOpen={onOpen} />)}
        {column && column.leads.length < total && !hidden && (
          <button type="button" className="ws-btn ws-btn--plain crm-col-more" onClick={onMore} disabled={column.loadingMore}>
            {column.loadingMore ? <Loader2 size={13} className="ws-spin" /> : null}Show {Math.min(PAGE, total - column.leads.length)} more
          </button>
        )}
      </div>
    </section>
  );
}

export default function LeadsBoard({ workspaceId, pipelineId, query, customFields = [], toolbar, onOpenLead, onEmptyAdd, onEmptyImport, refreshKey = 0 }) {
  const [stages, setStages] = useState(null);
  const [columns, setColumns] = useState({});
  const [error, setError] = useState("");
  const [activeLead, setActiveLead] = useState(null);
  const version = useRef(0);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor));
  const headers = useMemo(() => (workspaceId ? { "X-Workspace-ID": workspaceId } : undefined), [workspaceId]);
  const stageFilter = useMemo(() => (query?.stage_ids || []).map(Number), [query]);
  const valueField = useMemo(() => customFields.find((field) => field.is_active && ["number", "currency", "decimal", "integer"].includes(field.field_type) && VALUE_FIELD.test(`${field.key} ${field.label}`)), [customFields]);
  const chipField = useMemo(() => customFields.find((field) => field.is_active && field.field_type === "single_select" && field.is_table_visible), [customFields]);

  const fetchColumn = useCallback((stageId, page = 1) => axiosInstance.get("/leads", {
    headers,
    params: { ...query, stage_ids: [stageId], pipeline_id: pipelineId, per_page: PAGE, page },
  }).then((response) => ({ leads: response.data?.data || [], total: Number(response.data?.meta?.total ?? (response.data?.data || []).length) })), [headers, pipelineId, query]);

  const load = useCallback(async () => {
    if (!pipelineId) return;
    const run = ++version.current;
    setError("");
    setColumns({});
    try {
      const overview = await axiosInstance.get("/analytics/crm-overview", { headers, params: { pipeline_id: pipelineId, days: 7, mode: "queue" } });
      const list = (overview.data?.data?.funnel || []).map((stage) => ({ ...stage }));
      if (run !== version.current) return;
      setStages(list);
      await Promise.all(list.map(async (stage) => {
        if (stageFilter.length && !stageFilter.includes(Number(stage.id))) return;
        try {
          const result = await fetchColumn(stage.id);
          if (run === version.current) setColumns((current) => ({ ...current, [stage.id]: result }));
        } catch {
          if (run === version.current) setColumns((current) => ({ ...current, [stage.id]: { leads: [], total: 0, error: true } }));
        }
      }));
    } catch (loadError) {
      if (run === version.current) { setStages([]); setError(apiMessage(loadError, "We couldn’t load this pipeline’s stages.")); }
    }
  }, [fetchColumn, headers, pipelineId, stageFilter]);

  useEffect(() => { const timer = setTimeout(load, query?.search ? 250 : 0); return () => clearTimeout(timer); }, [load, refreshKey, query?.search]);
  useEffect(() => {
    const onReset = () => load();
    window.addEventListener("leads-reset", onReset);
    return () => window.removeEventListener("leads-reset", onReset);
  }, [load]);

  const tones = useMemo(() => stageTones(stages || []), [stages]);

  const loadMore = async (stageId) => {
    const column = columns[stageId];
    if (!column) return;
    setColumns((current) => ({ ...current, [stageId]: { ...column, loadingMore: true } }));
    try {
      const page = Math.floor(column.leads.length / PAGE) + 1;
      const result = await fetchColumn(stageId, page);
      setColumns((current) => {
        const seen = new Set(current[stageId].leads.map((lead) => lead.id));
        return { ...current, [stageId]: { leads: [...current[stageId].leads, ...result.leads.filter((lead) => !seen.has(lead.id))], total: result.total } };
      });
    } catch {
      setColumns((current) => ({ ...current, [stageId]: { ...column, loadingMore: false } }));
    }
  };

  const onDragEnd = async ({ active, over }) => {
    setActiveLead(null);
    const lead = active?.data?.current?.lead;
    const target = over?.data?.current?.stage;
    if (!lead || !target || Number(target.id) === Number(lead.lead_stage_id)) return;
    const fromId = lead.lead_stage_id;
    const before = columns;
    const moved = { ...lead, lead_stage_id: target.id, stage: { ...(lead.stage || {}), id: target.id, name: target.name, probability: target.probability, is_closed: target.is_closed }, stage_changed_at: new Date().toISOString() };
    setColumns((current) => ({
      ...current,
      [fromId]: current[fromId] ? { ...current[fromId], leads: current[fromId].leads.filter((row) => row.id !== lead.id), total: Math.max(0, current[fromId].total - 1) } : current[fromId],
      [target.id]: current[target.id] ? { ...current[target.id], leads: [moved, ...current[target.id].leads], total: current[target.id].total + 1 } : current[target.id],
    }));
    try {
      await moveLeadToStage(lead.id, target.id);
      const tone = tones[target.id];
      if (tone?.kind === "won") toast.success(`${lead.name} is won. Nice work!`);
      else toast.success(`${lead.name} moved to ${target.name}`);
    } catch (moveError) {
      setColumns(before);
      toast.error(apiMessage(moveError, `Couldn’t move ${lead.name}. It stays in its stage.`));
    }
  };

  const totalShown = Object.values(columns).reduce((sum, column) => sum + (column?.total || 0), 0);
  const allLoaded = stages && stages.every((stage) => columns[stage.id] || (stageFilter.length && !stageFilter.includes(Number(stage.id))));

  return (
    <section className="ws-card crm-board-card" aria-label="Leads board">
      {toolbar && <div className="crm-table-toolbar crm-board-toolbar"><div className="crm-table-search">{toolbar}</div></div>}
      {error ? (
        <div className="crm-table-state"><div className="crm-state"><span className="ws-icon-tile ws-icon-tile--amber crm-state-icon" aria-hidden="true"><RefreshCw size={18} /></span><h3 className="ws-h3">The board couldn’t load</h3><p className="ws-muted">{error}</p><div className="crm-state-actions"><button type="button" className="ws-btn ws-btn--primary" onClick={load}>Try again</button></div></div></div>
      ) : stages && stages.length === 0 ? (
        <div className="crm-table-state"><div className="crm-state"><h3 className="ws-h3">This pipeline has no stages yet</h3><p className="ws-muted">Add stages in Pipelines, or switch to the list.</p></div></div>
      ) : allLoaded && totalShown === 0 && !query?.search && !Object.keys(query || {}).length ? (
        <div className="crm-table-state"><div className="crm-state"><h3 className="ws-h3">Bring in your first lead</h3><p className="ws-muted">Add one lead by hand or import your list from a spreadsheet. Each lead lands in the first stage.</p><div className="crm-state-actions"><button type="button" className="ws-btn ws-btn--primary" onClick={onEmptyAdd}>Add lead</button><button type="button" className="ws-btn ws-btn--quiet" onClick={onEmptyImport}>Import leads</button></div></div></div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={({ active }) => setActiveLead(active?.data?.current?.lead || null)} onDragCancel={() => setActiveLead(null)} onDragEnd={onDragEnd}
          accessibility={{ screenReaderInstructions: { draggable: "To move a lead, press space, use the arrow keys to choose a stage, then press space again." } }}>
          <div className="crm-board" role="list" aria-label="Stages">
            {(stages || [null, null, null, null]).map((stage, index) => (stage
              ? <div role="listitem" key={stage.id} className="crm-col-wrap"><Column stage={stage} tone={tones[stage.id]} column={columns[stage.id]} valueField={valueField} chipField={chipField} onOpen={onOpenLead} onMore={() => loadMore(stage.id)} hidden={Boolean(stageFilter.length && !stageFilter.includes(Number(stage.id)))} /></div>
              : <div key={index} className="crm-col-wrap"><span className="ws-skeleton crm-col-skel" /></div>))}
          </div>
          <DragOverlay dropAnimation={{ duration: 180 }}>{activeLead ? <LeadCard lead={activeLead} valueField={valueField} chipField={chipField} overlay /> : null}</DragOverlay>
        </DndContext>
      )}
      <p className="crm-board-hint ws-muted">Drag a card to another stage to move the lead. Click it to open the lead.</p>
    </section>
  );
}
